import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GEONORGE_MUNICIPALITIES, GEONORGE_COUNTIES_WITH_MUNICIPALITIES } from 'src/constants/hook-keys';
import { Municipalities2023Map } from '../constants/municipalities2023';

export type Municipality = {
  number: string;
  name: string;
};

export type CountyWithMunicipalities = {
  fylkesnavn: string;
  fylkesnummer: string;
  kommuner: Municipalities[];
};

export type Municipalities = {
  kommunenavnNorsk: string;
  kommunenummer: string;
};

type MunicipalityLookup = {
  municipalities: Municipality[];
  municipalityMap: Map<string, string>;
};

const EMPTY_LOOKUP: MunicipalityLookup = { municipalities: [], municipalityMap: new Map() };

// Keyed on the cached response so the ~350-entry map is built once and shared by every consumer,
// rather than once per component instance.
const municipalityLookupCache = new WeakMap<object, MunicipalityLookup>();

const selectMunicipalityLookup = (data: Municipalities[]): MunicipalityLookup => {
  const cached = municipalityLookupCache.get(data);
  if (cached) return cached;

  const municipalities = data.map((x) => ({
    number: x.kommunenummer,
    name: x.kommunenavnNorsk,
  }));

  const lookup: MunicipalityLookup = {
    municipalities,
    municipalityMap: new Map(municipalities.map((municipality) => [municipality.number, municipality.name])),
  };

  municipalityLookupCache.set(data, lookup);
  return lookup;
};

const useGeonorge = () => {
  const countiesWithMunicipalitiesQuery = useQuery({
    queryKey: [GEONORGE_COUNTIES_WITH_MUNICIPALITIES],
    queryFn: () =>
      fetch(
        'https://api.kartverket.no/kommuneinfo/v1/fylkerkommuner?filtrer=fylkesnavn%2Cfylkesnummer%2Ckommuner.kommunenavnNorsk%2Ckommuner.kommunenummer',
      ).then((data) => data.json()),
    retry: 1,
    staleTime: 30 * 24 * 60 * 60 * 1000,
  });

  const municipalityQuery = useQuery({
    queryKey: [GEONORGE_MUNICIPALITIES],
    queryFn: (): Promise<Municipalities[]> =>
      fetch('https://ws.geonorge.no/kommuneinfo/v1/kommuner').then((data) => data.json()),
    select: selectMunicipalityLookup,
    retry: 1,
    staleTime: 30 * 24 * 60 * 60 * 1000,
  });

  const { municipalities, municipalityMap } = municipalityQuery.data ?? EMPTY_LOOKUP;

  const countiesWithMunicipalities: CountyWithMunicipalities[] = useMemo(() => {
    if (!countiesWithMunicipalitiesQuery.data) {
      return [];
    }
    return countiesWithMunicipalitiesQuery.data;
  }, [countiesWithMunicipalitiesQuery.data]);

  const getMunicipalityNameByNumber = useCallback(
    (number: string | undefined) => {
      if (number == undefined) return null;
      let name = municipalityMap.get(number);
      if (!name) {
        // Check if municipality name is in the 2023 municipality list
        name = Municipalities2023Map.get(number);
      }

      return name;
    },
    [municipalityMap],
  );

  const getMunicipalityNumberByName = useCallback(
    (name: string | undefined) => {
      if (name == undefined) return null;
      const number = municipalities.find(
        (municipality) => municipality.name.toLowerCase() === name.toLowerCase(),
      )?.number;

      return number;
    },
    [municipalities],
  );

  return {
    municipalities,
    countiesWithMunicipalities,
    getMunicipalityNameByNumber,
    getMunicipalityNumberByName,
  };
};

export default useGeonorge;
